// TradrQuant Academy — full lesson content.
// Educational reference. Pattern statistics reference Thomas Bulkowski (thepatternsite.com)
// and widely published technical-analysis literature. Not financial advice.

export const CATEGORY_LABELS = {
  foundations: "Chart Foundations",
  patterns: "The Pattern Library",
  signals: "Signals & Confirmation",
  risk: "Risk Management",
  library: "Pattern Deep-Dives",
};

export const LESSONS = [
  // ── FOUNDATIONS ─────────────────────────────────────────────
  {
    slug: "how-to-read-a-crypto-chart",
    category: "foundations",
    title: "How to read a crypto chart",
    summary:
      "The complete beginner's walkthrough: axes, candles, timeframes and the six-step process professionals use to read any chart in under a minute.",
    readMins: 7,
    takeaways: [
      "Price is the vertical axis, time is the horizontal axis — every chart is just those two facts.",
      "Read the higher timeframe first for the trend, then drop down to time the entry.",
      "A candle encodes four prices: open, high, low and close.",
      "Structure (higher highs / lower lows) beats indicators for reading direction.",
    ],
    blocks: [
      {
        h: "The two axes",
        p: [
          "Every price chart plots price on the vertical (y) axis and time on the horizontal (x) axis. As you move right, time passes; as the line or candles move up, price rises. That is the entire foundation — everything else is a way of summarising what happened between two points in time.",
        ],
      },
      {
        h: "Reading a candlestick",
        p: [
          "Most crypto charts use Japanese candlesticks. Each candle represents one period — a 5-minute candle covers five minutes, a daily candle covers a day. The rectangular body spans the open and close; the thin wicks (or shadows) mark the high and low reached during that period.",
          "A green (or hollow) candle closed higher than it opened — buyers won the period. A red (or filled) candle closed lower — sellers won. Long bodies show conviction; long wicks show rejection, where price was tried and pushed back.",
        ],
      },
      {
        h: "The six-step read",
        list: [
          "1. Trend — is price making higher highs and higher lows (up), the reverse (down), or neither (range)?",
          "2. Structure — mark the obvious swing highs and lows; those are your reference points.",
          "3. Support & resistance — draw the levels price keeps reacting to.",
          "4. Pattern — is a recognisable structure (double bottom, triangle, flag) forming at a level?",
          "5. Volume — is participation confirming or fading?",
          "6. Context — where does the higher timeframe say we are in the bigger move?",
        ],
      },
      {
        h: "Why the higher timeframe comes first",
        p: [
          "A setup that looks bullish on the 5-minute chart can be a small bounce inside a daily downtrend. Reading top-down — daily, then 4-hour, then your entry timeframe — keeps you trading with the dominant flow instead of against it. This is the single habit that separates consistent readers from screen-watchers.",
        ],
      },
    ],
    mistakes: [
      "Staring at a single low timeframe and mistaking noise for a trend.",
      "Adding five indicators before you can read raw price and volume.",
      "Ignoring the higher-timeframe trend because the entry chart looks exciting.",
    ],
    faqs: [
      { q: "What timeframe should a beginner use?", a: "Start on the daily and 4-hour charts. They filter out most noise and make trend and structure obvious before you ever look at fast timeframes." },
      { q: "Candlesticks or line chart?", a: "Line charts show the closing path and are great for spotting the trend at a glance; candlesticks add the open, high and low, which you need for patterns and entries." },
      { q: "Do I need indicators to read a chart?", a: "No. Price structure, support/resistance and volume are enough to read most charts. Indicators are optional confirmation, not the starting point." },
    ],
  },
  {
    slug: "anatomy-of-a-candlestick",
    category: "foundations",
    title: "Anatomy of a candlestick",
    summary:
      "Bodies, wicks and what they reveal about the fight between buyers and sellers — plus the handful of single candles worth memorising.",
    readMins: 6,
    diagram: { type: "candles", caption: "A bullish and a bearish candle — body (open→close) and wicks (high/low)." },
    takeaways: [
      "Body = open-to-close. Wick = the high and low extremes.",
      "Long wicks signal rejection; long bodies signal conviction.",
      "Context (where the candle prints) matters more than the candle itself.",
    ],
    blocks: [
      {
        h: "The four prices",
        p: [
          "A candlestick packs four data points into one shape: the open (where the period started), the close (where it ended), the high and the low. The body is drawn between open and close; the wicks stretch to the high and low.",
        ],
      },
      {
        h: "What the shape tells you",
        list: [
          "Long green body — strong buying, little selling pushback.",
          "Long red body — strong selling.",
          "Long lower wick (pin/hammer) — sellers pushed price down but buyers reclaimed it; potential rejection of lower prices.",
          "Long upper wick — buyers pushed up but sellers slammed it back; potential rejection of higher prices.",
          "Small body (doji) — indecision; open and close nearly equal.",
        ],
      },
      {
        h: "Single candles worth knowing",
        p: [
          "The hammer (long lower wick at a low) and shooting star (long upper wick at a high) flag rejection. The engulfing candle — a body that fully covers the previous candle's body — signals a shift in control. None of these are signals on their own; they matter when they appear at a level or after an extended move.",
        ],
      },
    ],
    mistakes: [
      "Trading a hammer in the middle of nowhere — location is everything.",
      "Treating a single candle as a guaranteed reversal.",
      "Ignoring the close: intrabar spikes lie, closes tell the truth.",
    ],
    faqs: [
      { q: "What is a doji?", a: "A candle whose open and close are almost identical, leaving a tiny body. It shows indecision — a pause in the current battle between buyers and sellers." },
      { q: "Why do wicks matter?", a: "Wicks show prices the market tried and rejected. A long wick means one side attempted a move and got overpowered — useful for spotting turning points at key levels." },
    ],
  },
  {
    slug: "support-and-resistance",
    category: "foundations",
    title: "Support & resistance",
    summary:
      "How to find the levels price actually respects, why they flip roles on a break, and how to trade the reaction instead of guessing.",
    readMins: 7,
    diagram: { type: "sr", caption: "Price oscillating between a resistance ceiling and a support floor." },
    takeaways: [
      "Support is where buyers repeatedly step in; resistance is where sellers do.",
      "The more reactions a level has, the more it matters.",
      "On a clean break, support becomes resistance and vice-versa (role reversal).",
      "Trade the reaction at the level, not a naked guess in open space.",
    ],
    blocks: [
      {
        h: "What a level really is",
        p: [
          "A support or resistance level is simply a price where the order flow has repeatedly turned. It is not a magic line — it is a zone where enough buyers or sellers have historically committed to change direction. Draw levels off obvious swing highs and lows and off prices that have been tested more than once.",
        ],
      },
      {
        h: "Role reversal",
        p: [
          "When price closes decisively through resistance, that old ceiling often becomes the new floor — buyers who missed the breakout defend it on the retest. The same works in reverse for broken support. This flip is one of the highest-quality entries in technical analysis because it gives you a defined level to lean your stop against.",
        ],
      },
      {
        h: "How to trade it",
        list: [
          "Mark the zone, then wait — do not front-run the level.",
          "Look for a reaction: a rejection wick, a lower-timeframe reversal, or a volume spike.",
          "Enter on confirmation with a stop just beyond the zone, where your idea is proven wrong.",
        ],
      },
    ],
    mistakes: [
      "Drawing dozens of lines until every price is 'a level'.",
      "Placing stops exactly on the level where liquidity gets hunted — give it room.",
      "Assuming a level holds forever; every level eventually breaks.",
    ],
    faqs: [
      { q: "Should levels be lines or zones?", a: "Zones. Price rarely reverses at the exact tick — draw a band around the swing points and treat it as an area of interest." },
      { q: "How many times must a level be tested to be valid?", a: "Two touches define a level; a third reaction confirms it. But each successful test also slightly weakens it for next time." },
    ],
  },

  // ── PATTERN LIBRARY ─────────────────────────────────────────
  {
    slug: "reversal-patterns",
    category: "patterns",
    title: "Reversal patterns",
    summary:
      "Double bottoms, head & shoulders and triple bottoms — how to spot the end of a move, and why the neckline close is the only thing that confirms it.",
    readMins: 8,
    diagram: {
      type: "pattern", bull: true,
      path: "M10,25 L55,95 L100,55 L150,95 L200,55 L232,20",
      level: 55, points: [[55, 95], [150, 95]], breakout: [214, 40],
      caption: "Double bottom (W) — two equal lows, neckline break confirms.",
    },
    stats: [
      { label: "Double bottom break-even rate", value: "84%" },
      { label: "Head & shoulders break-even rate", value: "81%" },
      { label: "Triple bottom average rise", value: "+46%" },
      { label: "H&S sample (Bulkowski)", value: "1,000+" },
    ],
    takeaways: [
      "Reversals mark the exhaustion of the prior trend, not just a pause.",
      "They only confirm on a close beyond the neckline — never anticipate.",
      "Measured target = pattern height projected from the breakout.",
      "Volume usually fades into the pattern and spikes on the break.",
    ],
    blocks: [
      {
        h: "What a reversal pattern is",
        p: [
          "A reversal pattern forms at the end of a trend and signals that control is changing hands. After an extended move, momentum fades, price carves out a recognisable structure, and a break of that structure's neckline confirms the new direction.",
        ],
      },
      {
        h: "The three you must know",
        list: [
          "Double bottom (W) — two troughs at a similar low; confirms on a close above the middle peak. ~84% break-even rate, ~39% average rise in Bulkowski's data.",
          "Head & shoulders — three peaks, the middle (head) highest; a bearish top that confirms on a neckline breakdown. ~81% break-even rate.",
          "Triple bottom — three failed tests of support; a firm base that confirms on a resistance break. Ranked highly among bullish reversals.",
        ],
      },
      {
        h: "The trading playbook",
        list: [
          "Entry — on the confirmed close beyond the neckline, or on the retest of the neckline as new support/resistance.",
          "Stop — beyond the last swing that would invalidate the pattern (below the second bottom, above the right shoulder).",
          "Target — measure the pattern height (neckline to extreme) and project it from the breakout point.",
        ],
      },
    ],
    mistakes: [
      "Buying the second bottom before the neckline breaks — that is a guess, not a pattern.",
      "Ignoring the higher-timeframe trend the reversal is fighting.",
      "Forgetting that a break on weak volume often fails and retests.",
    ],
    faqs: [
      { q: "When is a double bottom confirmed?", a: "Only when price closes above the peak between the two troughs (the neckline). Before that, it is just two lows." },
      { q: "What is the target for a head & shoulders?", a: "Measure the distance from the head to the neckline, then project that same distance below the neckline breakdown." },
      { q: "Are reversal patterns reliable?", a: "The best ones (double/triple bottoms, inverse H&S) show 80%+ break-even rates in Bulkowski's samples — but only when confirmed and traded with a stop." },
    ],
  },
  {
    slug: "continuation-patterns",
    category: "patterns",
    title: "Continuation patterns",
    summary:
      "Flags, pennants and triangles are pauses inside a trend. Learn to trade the break in the trend's direction — and why pennants underperform flags.",
    readMins: 8,
    diagram: {
      type: "pattern", bull: true,
      path: "M10,108 L40,26 L64,42 L90,34 L114,50 L140,42 L164,56 L232,16",
      level: null, points: [[40, 26]], breakout: [190, 34],
      caption: "Bull flag — a sharp pole, a tight drift, then continuation up.",
    },
    stats: [
      { label: "Bull flag average rise (high-tight)", value: "+39%" },
      { label: "Ascending triangle break-even rate", value: "83%" },
      { label: "Bull pennant upward break rate", value: "57%" },
      { label: "Volume decline during formation", value: "~86%" },
    ],
    takeaways: [
      "Continuations are consolidations inside an existing trend, not reversals.",
      "Trade the break in the direction of the prior thrust (the flagpole).",
      "Volume dries up in the pause and spikes on the breakout.",
      "Flags generally outperform pennants on Bulkowski's metrics.",
    ],
    blocks: [
      {
        h: "The idea",
        p: [
          "After a sharp move (the flagpole), the market pauses to digest gains before continuing. That pause forms a tidy shape — a flag (parallel lines), a pennant (converging lines) or a triangle. The prior trend usually resumes on the breakout.",
        ],
      },
      {
        h: "Flag vs pennant vs triangle",
        list: [
          "Bull flag — a small downward-sloping parallel channel after a rally; the high-and-tight variant is one of the best-performing patterns.",
          "Bull pennant — a small symmetrical triangle after a rally; breaks up only ~57% of the time, so demand extra confirmation.",
          "Ascending triangle — flat resistance with rising support; buyers stepping up, ~83% break-even rate.",
        ],
      },
      {
        h: "Playbook",
        list: [
          "Entry — on a close above the upper trendline with rising volume, or on the retest of that line as support.",
          "Stop — below the pattern low, the lower trendline, or the flagpole midpoint.",
          "Target — project the flagpole height from the breakout point.",
        ],
      },
    ],
    mistakes: [
      "No flagpole: a consolidation without a preceding sharp move is not a flag or pennant.",
      "Letting the pattern grow too large — a multi-week 'pennant' is just a range.",
      "Trading a bull continuation inside a confirmed downtrend.",
    ],
    faqs: [
      { q: "What is the difference between a bull flag and a bull pennant?", a: "A flag has parallel trendlines; a pennant has converging trendlines. Flags tend to outperform pennants on break-even and target-hit rates." },
      { q: "How do I set a target on a flag?", a: "Measure the height of the flagpole (the sharp move before the pause) and project that distance from the breakout point." },
      { q: "Do continuation patterns always continue?", a: "No. Even good continuations fail — pennants break up only ~57% of the time — which is why volume confirmation and a stop are mandatory." },
    ],
  },
  {
    slug: "break-even-rate",
    category: "patterns",
    title: "Why the break-even rate matters",
    summary:
      "The number most sites hide. What a pattern's 'break-even failure rate' actually means, and how to turn it into position size and expectancy.",
    readMins: 6,
    stats: [
      { label: "Bulkowski trade database", value: "38,500+" },
      { label: "Best bullish patterns", value: "80%+" },
      { label: "Symmetrical triangle rank", value: "36 / 39" },
    ],
    takeaways: [
      "A pattern's real edge is its break-even rate, not a marketing 'win rate'.",
      "Break-even failure rate = share of trades that fail to move even 5% before reversing.",
      "Edge only exists when rate × average move beats your losses (expectancy).",
      "We show the rate on every alert so you size to the odds.",
    ],
    blocks: [
      {
        h: "What break-even rate means",
        p: [
          "Thomas Bulkowski measured tens of thousands of patterns and reported a 'break-even failure rate' — the percentage of occurrences that failed to move even 5% in the expected direction before reversing. A 54% break-even failure rate means barely half of those patterns delivered a meaningful move.",
          "That is a very different number from the glossy 'win rates' many tools advertise. It is honest, and it is the number that should drive your decisions.",
        ],
      },
      {
        h: "Turning it into expectancy",
        p: [
          "Edge is not win rate alone — it is win rate combined with how much you win versus lose. Expectancy = (win% × average win) − (loss% × average loss). A pattern that only completes 55% of the time can still be highly profitable if its average winner is several times its average loser.",
        ],
      },
      {
        h: "How we use it",
        p: [
          "TradrQuant surfaces each pattern's measured completion rate on every detection, so you can size smaller on marginal setups and lean in on the high-rate ones — instead of treating every alert as equal.",
        ],
      },
    ],
    mistakes: [
      "Chasing patterns with high names but poor break-even rates (e.g. symmetrical triangles).",
      "Confusing 'breaks in my direction' with 'reaches a profitable target'.",
      "Sizing every trade the same regardless of the pattern's odds.",
    ],
    faqs: [
      { q: "Is a higher break-even rate always better?", a: "It helps, but expectancy matters more. A lower-rate pattern with a large average move can beat a high-rate pattern with a tiny move." },
      { q: "Where do these numbers come from?", a: "They reference Thomas Bulkowski's published statistics (thepatternsite.com), the most widely cited pattern-performance database in technical analysis." },
    ],
  },

  // ── SIGNALS & CONFIRMATION ──────────────────────────────────
  {
    slug: "volume-confirmation",
    category: "signals",
    title: "Volume confirmation",
    summary:
      "Volume is the fuel behind every move. How to use it to separate real breakouts from traps, and the exact volume behaviour to expect in each pattern.",
    readMins: 6,
    takeaways: [
      "Breakouts on rising volume tend to hold; on fading volume they tend to fail.",
      "Volume typically dries up inside a consolidation and spikes on the break.",
      "Volume confirms — it rarely leads. Use it to validate, not to predict.",
    ],
    blocks: [
      {
        h: "Why volume matters",
        p: [
          "Price tells you what happened; volume tells you how much conviction was behind it. A breakout backed by a surge of volume means fresh participants are committing capital. A breakout on thin volume often means a handful of orders pushed price through an empty book — exactly the setup for a fake-out.",
        ],
      },
      {
        h: "What healthy patterns look like",
        list: [
          "Consolidations (flags, pennants, triangles) — volume declines as the pattern tightens.",
          "Breakout — a clear volume spike above the recent average.",
          "Retest — volume fades again as price pulls back, then picks up as the trend resumes.",
        ],
      },
      {
        h: "Reading volume in crypto",
        p: [
          "Crypto trades 24/7 across many venues, so volume can be noisy and exchange-specific. Compare current volume to its own recent average rather than to an absolute number, and prefer the aggregated volume of a liquid pair over a thin altcoin where a single whale can distort the picture.",
        ],
      },
    ],
    mistakes: [
      "Trusting a breakout with no volume expansion.",
      "Comparing volume across illiquid pairs instead of to its own average.",
      "Expecting volume to predict — it confirms after the fact.",
    ],
    faqs: [
      { q: "What is a volume spike?", a: "A candle whose volume is clearly above the recent average — often 1.5x or more. On a breakout, it signals genuine participation." },
      { q: "Can a breakout be valid without volume?", a: "It can, but the odds drop sharply. Low-volume breakouts fail and retest far more often, so demand confirmation or trade smaller." },
    ],
  },
  {
    slug: "multi-timeframe-analysis",
    category: "signals",
    title: "Multi-timeframe analysis",
    summary:
      "The top-down workflow that stops you fighting the trend: align the higher timeframe with your entry, use the 1:4 ratio, and grade every setup by context.",
    readMins: 7,
    takeaways: [
      "Trade in the direction of the higher-timeframe trend.",
      "Use roughly a 1:4 ratio between your context and entry timeframes.",
      "A great pattern against the higher-timeframe trend is a low-quality setup.",
    ],
    blocks: [
      {
        h: "The top-down process",
        p: [
          "Start high and work down. The higher timeframe (daily/4-hour) sets the trend and the key levels. The middle timeframe refines structure. The lower timeframe (15-minute/5-minute) is only for timing the entry once the higher timeframes agree.",
        ],
      },
      {
        h: "The 1:4 ratio",
        p: [
          "A common guideline is to pair timeframes about four to six times apart — e.g. daily for context and 4-hour for entries, or 1-hour for context and 15-minute for entries. This keeps the two views meaningfully different without jumping so far that they lose relation.",
        ],
      },
      {
        h: "Grading a setup by context",
        list: [
          "Best — pattern direction matches the higher-timeframe trend and forms at a higher-timeframe level.",
          "Okay — pattern matches the trend but forms in open space.",
          "Avoid — pattern fights the higher-timeframe trend (a bullish setup inside a daily downtrend).",
        ],
      },
    ],
    mistakes: [
      "Zooming into a fast chart and forgetting the daily trend entirely.",
      "Using two timeframes that are almost identical (1h and 2h).",
      "Taking counter-trend setups without accepting the lower odds.",
    ],
    faqs: [
      { q: "How many timeframes should I use?", a: "Two or three: one for context, one for the setup, and optionally one for the precise entry. More than that usually creates analysis paralysis." },
      { q: "Which timeframe wins if they disagree?", a: "The higher timeframe. It represents more capital and more participants, so it dominates the lower one." },
    ],
  },
  {
    slug: "fake-breakouts",
    category: "signals",
    title: "How to spot fake breakouts",
    summary:
      "Three checks before you enter — volume, retest and the order book — so you stop getting trapped by the move that snaps straight back inside the range.",
    readMins: 6,
    takeaways: [
      "A real breakout holds the reclaimed level; a fake one snaps back within a candle or two.",
      "Check volume, then the retest, then the order book — in that order.",
      "Waiting for the retest costs you a little edge but avoids most traps.",
    ],
    blocks: [
      {
        h: "Why fake-outs happen",
        p: [
          "Obvious levels attract clustered stop orders and breakout traders. Larger players know this, so price is often pushed just beyond a level to trigger that liquidity before reversing — a stop hunt. The result is a breakout that fails almost immediately.",
        ],
      },
      {
        h: "The three checks",
        list: [
          "1. Volume — did the break come with a genuine volume expansion, or a whimper?",
          "2. Retest — does price pull back and hold the broken level as new support/resistance? A clean hold confirms.",
          "3. Order book / structure — is there resting liquidity defending the new side, or did price immediately close back inside the range?",
        ],
      },
      {
        h: "Trade the retest, not the poke",
        p: [
          "Entering on the first candle beyond a level is the most trapped entry in trading. Waiting for a close beyond the level and a successful retest sacrifices a few percent of the move but filters out the majority of fake-outs.",
        ],
      },
    ],
    mistakes: [
      "Entering on an intrabar wick beyond the level instead of a close.",
      "Ignoring that price closed back inside the range within one candle.",
      "Setting stops right at the level where they get hunted.",
    ],
    faqs: [
      { q: "How quickly does a fake breakout reverse?", a: "Usually within one to three candles. If price cannot hold the reclaimed level almost immediately, treat the break as suspect." },
      { q: "Is waiting for a retest always better?", a: "It misses the occasional runaway breakout, but over many trades it dramatically improves your hit rate by filtering traps." },
    ],
  },

  // ── RISK MANAGEMENT ─────────────────────────────────────────
  {
    slug: "position-sizing",
    category: "risk",
    title: "Position sizing",
    summary:
      "The math that keeps you in the game: risk a fixed small percentage per trade, let your stop set your size, and survive the inevitable losing streak.",
    readMins: 6,
    takeaways: [
      "Risk a fixed, small % of your account per trade (commonly 0.5–2%).",
      "Your stop distance sets your position size — not your conviction.",
      "Size = (account × risk%) ÷ (entry − stop distance).",
    ],
    blocks: [
      {
        h: "Risk first, size second",
        p: [
          "Professionals decide how much they are willing to lose before they think about how much they can make. Fix a per-trade risk — say 1% of the account — and then calculate the position size that keeps the loss at exactly that amount if your stop is hit.",
        ],
      },
      {
        h: "The formula",
        p: [
          "Position size = (account equity × risk per trade) ÷ (distance from entry to stop). If you have $10,000, risk 1% ($100), and your stop is 5% away, your position is $100 ÷ 0.05 = $2,000. The wider the stop, the smaller the position — automatically.",
        ],
      },
      {
        h: "Surviving variance",
        p: [
          "Even a 60%-win strategy will string together losers. Risking 1% means a rare 10-loss streak costs about 10% — recoverable. Risking 10% per trade turns the same streak into account-ending damage. Small, consistent risk is what lets an edge play out.",
        ],
      },
    ],
    mistakes: [
      "Sizing by 'how sure I feel' instead of by stop distance.",
      "Risking a large percentage to make a streak back faster.",
      "Ignoring leverage — it multiplies both the position and the risk.",
    ],
    faqs: [
      { q: "How much should I risk per trade?", a: "Most professionals risk 0.5–2% of account equity per trade. Beginners should stay at the low end while they build consistency." },
      { q: "How does leverage change sizing?", a: "Leverage changes margin, not risk math. Size to your stop distance and intended dollar risk; leverage just determines the margin you post." },
    ],
  },
  {
    slug: "stop-placement",
    category: "risk",
    title: "Stop placement",
    summary:
      "Put the stop where your idea is wrong — not at a round number. Structure-based stops, why to avoid obvious liquidity, and the trade-off with size.",
    readMins: 6,
    takeaways: [
      "Place the stop where the setup is invalidated, then size to it.",
      "Avoid round numbers and the exact level where stops cluster.",
      "A tighter stop means a smaller position for the same dollar risk.",
    ],
    blocks: [
      {
        h: "Invalidation, not hope",
        p: [
          "A stop marks the price at which your reason for the trade no longer holds — below the second bottom of a double bottom, beyond the pattern's trendline, past the swing that structure says should not break. If price gets there, the setup failed; the stop is simply respecting that fact.",
        ],
      },
      {
        h: "Avoid the obvious",
        p: [
          "The exact level everyone can see — the pattern low, a round number like $60,000 — is where resting stop orders pile up and where price is most likely to be pushed to trigger them. Give the level a small buffer so a routine liquidity sweep does not stop you out of a trade that was actually correct.",
        ],
      },
      {
        h: "The size trade-off",
        p: [
          "Tighter stops let you size larger for the same risk, but they get hit by noise more often. Wider stops survive noise but force smaller positions. Match the stop to the structure and let position size adjust — never widen a stop just to avoid being wrong.",
        ],
      },
    ],
    mistakes: [
      "Moving a stop further away to avoid taking the loss.",
      "Placing the stop exactly on the visible level with no buffer.",
      "Using a fixed dollar/percent stop that ignores where structure actually breaks.",
    ],
    faqs: [
      { q: "Should I use a mental stop or a hard stop?", a: "A hard (resting) stop is safer for most traders — it removes emotion in the moment. Mental stops only work with proven discipline." },
      { q: "How far beyond the level should my stop sit?", a: "Just far enough to survive a normal liquidity sweep — often a fraction of the average candle range beyond the structure, not an arbitrary round figure." },
    ],
  },
  {
    slug: "risk-reward",
    category: "risk",
    title: "Risk / reward",
    summary:
      "Drop the 1:3 cliché. How to set targets from the pattern's measured move and its historical target-hit rate, and why expectancy beats a fixed ratio.",
    readMins: 6,
    takeaways: [
      "A fixed 1:3 target is a rule of thumb, not a strategy.",
      "Set targets from the pattern's measured move and its target-hit rate.",
      "Expectancy = win% × avg win − loss% × avg loss — that is the real edge.",
    ],
    blocks: [
      {
        h: "Beyond the cliché",
        p: [
          "The '1:3 risk-reward' rule is a decent starting default, but blindly demanding it can push your target into a wall of resistance the pattern will never reach. Better to derive the target from the setup itself — the measured move — and then check whether the reward-to-risk it implies is worth taking.",
        ],
      },
      {
        h: "Measured moves and target-hit rates",
        p: [
          "Each pattern has a measured target (project the pattern height from the breakout) and a historical target-hit rate. Flags, for example, often only reach about half their measured move, which is why experienced traders bank partial profits early. Use the pattern's real statistics rather than a one-size-fits-all ratio.",
        ],
      },
      {
        h: "Expectancy is the goal",
        p: [
          "You do not need to win most trades — you need positive expectancy. Combining a realistic target, an honest win rate and disciplined stops produces an edge that compounds. A 40%-win system with 3:1 average reward is highly profitable; a 70%-win system with 1:3 reward loses money.",
        ],
      },
    ],
    mistakes: [
      "Forcing a 1:3 target into a level the pattern cannot reach.",
      "Judging a trade by its outcome instead of whether it had positive expectancy.",
      "Never banking partial profit on patterns that rarely hit full target.",
    ],
    faqs: [
      { q: "Is a 1:2 risk-reward good enough?", a: "It can be — at a 50% win rate, 1:2 is strongly profitable. The right ratio depends on your win rate; solve for expectancy, not a fixed number." },
      { q: "Should I take partial profits?", a: "For patterns with low target-hit rates (like flags and pennants), scaling out near the halfway measured move locks in gains the full target often never reaches." },
    ],
  },
  // ── PATTERN DEEP-DIVES (single patterns) ────────────────────
  {
    slug: "head-and-shoulders-pattern",
    category: "library",
    title: "Head & shoulders pattern",
    summary:
      "The most recognised reversal in trading: three peaks with a higher middle, a neckline, and a measured downside target. ~81% break-even rate on Bulkowski's data.",
    readMins: 8,
    diagram: {
      type: "pattern", bull: false,
      path: "M10,95 L45,55 L80,90 L120,25 L160,90 L195,58 L230,105",
      level: 90, points: [[45, 55], [120, 25], [195, 58]], breakout: [224, 102],
      caption: "Head & shoulders — left shoulder, higher head, right shoulder, neckline breakdown.",
    },
    stats: [
      { label: "Break-even rate (up-trend top)", value: "81%" },
      { label: "Average decline", value: "-16%" },
      { label: "Bulkowski rank (bearish)", value: "9 / 36" },
      { label: "Pullback / throwback rate", value: "~45%" },
    ],
    takeaways: [
      "Three peaks: the middle (head) is the highest; the two shoulders are roughly level.",
      "Confirmation is a close below the neckline drawn under the two troughs.",
      "Target = height from head to neckline, projected below the break.",
      "A pullback to the neckline after the break is common — and a second entry.",
    ],
    blocks: [
      {
        h: "How it forms",
        p: [
          "A head & shoulders top marks the transition from uptrend to downtrend. Buyers make a high (left shoulder), a higher high (head), then fail to exceed the head on the next push (right shoulder). Connecting the two lows between the peaks gives the neckline — the line that matters.",
        ],
      },
      {
        h: "The trading playbook",
        list: [
          "Entry — on a decisive close below the neckline, or on the throwback that retests the neckline from below as new resistance.",
          "Stop — above the right shoulder (or above the head for a wider, safer stop).",
          "Target — measure the vertical distance from the head to the neckline and project it down from the breakdown point.",
        ],
      },
      {
        h: "Volume & the inverse",
        p: [
          "Classic head & shoulders shows declining volume into the head and right shoulder, then a volume expansion on the breakdown. The mirror image at a market bottom — the inverse head & shoulders — is one of the most reliable bullish reversals and is traded exactly the same way, flipped.",
        ],
      },
    ],
    mistakes: [
      "Shorting the right shoulder before the neckline breaks — that is anticipation, not confirmation.",
      "Drawing the neckline off the wrong swing lows.",
      "Ignoring the higher-timeframe trend the top is trying to reverse.",
    ],
    faqs: [
      { q: "How reliable is the head & shoulders pattern?", a: "In Bulkowski's data the break-even failure rate is about 19% (≈81% break-even), ranking it 9th of 36 bearish patterns — one of the more dependable tops when confirmed." },
      { q: "What is the price target?", a: "Measure the distance from the top of the head down to the neckline, then project that same distance below the point where price breaks the neckline." },
      { q: "What is an inverse head & shoulders?", a: "The same shape flipped at a market bottom — three troughs with a lower middle. It confirms on a neckline breakout to the upside and is a bullish reversal." },
    ],
  },
  {
    slug: "double-bottom-pattern",
    category: "library",
    title: "Double bottom (W) pattern",
    summary:
      "Two failed attempts at the same low that flip a downtrend. The Eve & Eve variant ranks 5th of 39 bullish patterns; ~84% break-even, ~39% average rise.",
    readMins: 8,
    diagram: {
      type: "pattern", bull: true,
      path: "M10,22 L55,96 L100,52 L150,96 L200,52 L232,18",
      level: 52, points: [[55, 96], [150, 96]], breakout: [212, 38],
      caption: "Double bottom (W) — two equal troughs, confirmed on the neckline break.",
    },
    stats: [
      { label: "Break-even rate", value: "84%" },
      { label: "Average rise", value: "+39%" },
      { label: "Eve & Eve rank (bullish)", value: "5 / 39" },
      { label: "Throwback rate", value: "~64%" },
    ],
    takeaways: [
      "Two troughs at a similar low with a peak (neckline) between them.",
      "Confirmation is a close above that middle peak — not the second bounce.",
      "Target = pattern height projected up from the neckline break.",
      "Expect a throwback to the neckline before the real move — it is a second entry.",
    ],
    blocks: [
      {
        h: "How it forms",
        p: [
          "After a decline, price makes a low, bounces to form a peak, then falls again to roughly the same low and fails to break it. That double failure signals sellers are exhausted. The peak between the two lows is the neckline; a close above it confirms the reversal.",
        ],
      },
      {
        h: "The trading playbook",
        list: [
          "Entry — on the confirmed close above the neckline, or on the throwback that retests it as new support.",
          "Stop — below the lower of the two bottoms.",
          "Target — measure from the bottoms to the neckline and project that height above the breakout.",
        ],
      },
      {
        h: "Adam vs Eve",
        p: [
          "Bulkowski distinguishes sharp, narrow bottoms (Adam) from rounded, wide ones (Eve). The Eve & Eve double bottom — two rounded troughs — is the best-performing variant, ranking 5th of 39 bullish patterns.",
        ],
      },
    ],
    mistakes: [
      "Buying the second bottom before the neckline breaks.",
      "Requiring the two lows to be identical — a small difference is fine.",
      "Skipping the throwback entry and chasing the extended move.",
    ],
    faqs: [
      { q: "When is a double bottom confirmed?", a: "Only when price closes above the peak between the two lows (the neckline). Until then it is just two lows." },
      { q: "How high can it run?", a: "The measured target is the pattern height projected from the breakout, but the average rise on confirmed Eve & Eve double bottoms is around 39% in Bulkowski's sample." },
    ],
  },
  {
    slug: "bull-flag-pattern",
    category: "library",
    title: "Bull flag pattern",
    summary:
      "A sharp rally, a tight orderly pullback, then continuation. The high-and-tight variant is elite; standard bull flags carry a ~44% break-even failure rate — so confirmation matters.",
    readMins: 8,
    diagram: {
      type: "pattern", bull: true,
      path: "M10,110 L42,22 L66,40 L92,30 L118,48 L144,38 L170,54 L232,14",
      level: null, points: [[42, 22]], breakout: [196, 32],
      caption: "Bull flag — steep pole, small downward channel, breakout continuation.",
    },
    stats: [
      { label: "Average rise (standard)", value: "+9%" },
      { label: "High-and-tight average rise", value: "+39%" },
      { label: "Break-even failure (standard)", value: "~44%" },
      { label: "Sample (high-tight)", value: "1,028" },
    ],
    takeaways: [
      "A bull flag needs a flagpole — a sharp preceding rally.",
      "The flag is a small, orderly pullback (parallel, slightly down-sloping).",
      "Enter on the breakout above the flag with rising volume.",
      "Target = flagpole height projected from the breakout.",
    ],
    blocks: [
      {
        h: "How it forms",
        p: [
          "Price surges (the pole), then pauses in a tight, slightly downward-sloping channel as early buyers take profit. Volume dries up during the flag. When fresh demand overwhelms the drift, price breaks the upper trendline and resumes the advance.",
        ],
      },
      {
        h: "The trading playbook",
        list: [
          "Entry — on a close above the upper flag trendline with a volume spike, or on the retest of that line as support.",
          "Stop — below the flag low or the lower trendline.",
          "Target — project the flagpole height from the breakout point; consider banking partial profit at the halfway mark.",
        ],
      },
      {
        h: "High-and-tight flags",
        p: [
          "The high-and-tight flag — a near-vertical pole (price roughly doubling) followed by a shallow pullback — is one of the best-performing chart patterns Bulkowski measured, with an average rise around 39%. Standard bull flags are far more modest, which is why volume confirmation is essential.",
        ],
      },
    ],
    mistakes: [
      "Calling a random consolidation a 'flag' with no flagpole.",
      "Letting the flag drift too far or too long — deep, sloppy pullbacks fail.",
      "Entering before the breakout instead of on it.",
    ],
    faqs: [
      { q: "What is the difference between a bull flag and a bull pennant?", a: "A flag has parallel trendlines; a pennant has converging ones. Flags generally outperform pennants on Bulkowski's break-even and target metrics." },
      { q: "How do I set a bull-flag target?", a: "Measure the flagpole (the sharp move into the flag) and project that distance up from the breakout point." },
    ],
  },
  {
    slug: "cup-and-handle-pattern",
    category: "library",
    title: "Cup & handle pattern",
    summary:
      "A rounded base followed by a small pullback handle, then a breakout. Ranked 3rd of 39 bullish patterns by Bulkowski — averaging a 54% rise on 913 trades.",
    readMins: 8,
    diagram: {
      type: "pattern", bull: true,
      path: "M10,28 Q95,138 180,34 L206,30 L219,50 L231,34 L238,16",
      level: 30, points: [[180, 34]], breakout: [231, 34],
      caption: "Cup & handle — rounded cup, a small handle dip, then breakout.",
    },
    stats: [
      { label: "Average rise", value: "+54%" },
      { label: "Bulkowski rank (bullish)", value: "3 / 39" },
      { label: "Sample", value: "913" },
      { label: "Throwback rate", value: "~58%" },
    ],
    takeaways: [
      "A U-shaped 'cup' base signals a slow transfer from sellers to buyers.",
      "The 'handle' is a small pullback near the cup's rim.",
      "Confirmation is a breakout above the rim (resistance).",
      "Target = cup depth projected up from the breakout.",
    ],
    blocks: [
      {
        h: "How it forms",
        p: [
          "Price sells off and rounds out a gentle, U-shaped bottom (not a sharp V), recovering back to the prior high. It then drifts down slightly to form the handle as the last profit-takers exit. A breakout above the rim, ideally on rising volume, launches the continuation.",
        ],
      },
      {
        h: "The trading playbook",
        list: [
          "Entry — on a close above the rim/handle resistance with a volume expansion.",
          "Stop — below the handle low.",
          "Target — measure the depth of the cup and project it upward from the breakout.",
        ],
      },
      {
        h: "What makes a good cup",
        p: [
          "The best cups are rounded and reasonably symmetric — a sharp V-recovery is less reliable. The handle should be shallow (a minor pullback, not a new decline) and form in the upper half of the cup. Deep handles hint the base has failed.",
        ],
      },
    ],
    mistakes: [
      "Trading a V-shaped recovery as a cup — it needs a rounded base.",
      "Accepting a deep handle that erases most of the cup's gains.",
      "Entering inside the handle before the rim breaks.",
    ],
    faqs: [
      { q: "How long does a cup & handle take to form?", a: "It is usually a larger, slower pattern — often weeks to months on higher timeframes — but the same shape appears fractally on lower timeframes in crypto." },
      { q: "Where do I place the target?", a: "Measure the depth of the cup (rim down to the base) and add it to the breakout price above the rim." },
    ],
  },
  {
    slug: "ascending-triangle-pattern",
    category: "library",
    title: "Ascending triangle pattern",
    summary:
      "Flat resistance with rising support — buyers stepping up into a ceiling. ~83% break-even rate and a ~43% average rise; usually resolves to the upside.",
    readMins: 7,
    diagram: {
      type: "pattern", bull: true,
      path: "M10,100 L45,44 L78,74 L112,44 L142,62 L176,44 L206,52 L234,16",
      level: 44, points: [[45, 44], [112, 44], [176, 44]], breakout: [220, 34],
      caption: "Ascending triangle — flat resistance, higher lows, upside break.",
    },
    stats: [
      { label: "Break-even rate", value: "83%" },
      { label: "Average rise", value: "+43%" },
      { label: "Target hit rate", value: "~70%" },
      { label: "Typical breakout direction", value: "Up" },
    ],
    takeaways: [
      "A flat horizontal resistance with a rising trendline of higher lows.",
      "Higher lows show buyers getting more aggressive into the ceiling.",
      "Confirmation is a close above the flat resistance with volume.",
      "Target = triangle height projected up from the breakout.",
    ],
    blocks: [
      {
        h: "How it forms",
        p: [
          "Price repeatedly tests the same resistance level while each pullback bottoms higher than the last. That rising support squeezes price into the ceiling — buyers are willing to pay up sooner each time — and the pattern usually resolves with an upside breakout.",
        ],
      },
      {
        h: "The trading playbook",
        list: [
          "Entry — on a close above the flat resistance with a volume spike, or on the retest of that level as new support.",
          "Stop — below the most recent higher low or the rising trendline.",
          "Target — measure the tallest part of the triangle and project it up from the breakout.",
        ],
      },
      {
        h: "Ascending vs descending",
        p: [
          "The mirror image — a descending triangle (flat support, falling highs) — leans bearish, though it counter-intuitively breaks upward a meaningful share of the time. In both, trade the confirmed break, not your bias about which way it 'should' go.",
        ],
      },
    ],
    mistakes: [
      "Front-running the breakout inside the triangle.",
      "Ignoring a low-volume break that quickly fails.",
      "Assuming the flat side must break — always wait for the close.",
    ],
    faqs: [
      { q: "Which way does an ascending triangle break?", a: "Most often upward — the rising lows signal accumulating demand — but you still confirm with a close above resistance and volume." },
      { q: "What is the target?", a: "Measure the height of the triangle at its widest and project that distance up from the breakout level." },
    ],
  },
  {
    slug: "double-top-pattern",
    category: "library",
    title: "Double top (M) pattern",
    summary:
      "Two failed attempts at the same high that flip an uptrend. Ranked 19th of 36 bearish patterns; ~75% break-even rate and a ~15% average decline.",
    readMins: 7,
    diagram: {
      type: "pattern", bull: false,
      path: "M10,100 L55,26 L100,68 L150,26 L200,68 L232,104",
      level: 68, points: [[55, 26], [150, 26]], breakout: [214, 92],
      caption: "Double top (M) — two equal highs, confirmed on the valley breakdown.",
    },
    stats: [
      { label: "Break-even rate", value: "75%" },
      { label: "Average decline", value: "-15%" },
      { label: "Bulkowski rank (bearish)", value: "19 / 36" },
      { label: "Pullback rate", value: "~61%" },
    ],
    takeaways: [
      "Two peaks at a similar high with a valley (neckline) between them.",
      "Confirmation is a close below that valley low — not the second rejection.",
      "Target = pattern height projected down from the neckline break.",
      "A pullback to the neckline after the break is common and offers a second entry.",
    ],
    blocks: [
      {
        h: "How it forms",
        p: [
          "After an advance, price makes a high, pulls back to a valley, rallies again to roughly the same high and fails to exceed it. That double rejection signals buyers are exhausted. The valley between the peaks is the neckline; a close below it confirms the reversal to the downside.",
        ],
      },
      {
        h: "The trading playbook",
        list: [
          "Entry — on a decisive close below the valley neckline, or on the pullback that retests it as new resistance.",
          "Stop — above the higher of the two peaks.",
          "Target — measure from the peaks down to the neckline and project that height below the breakdown.",
        ],
      },
      {
        h: "Adam vs Eve",
        p: [
          "As with double bottoms, Bulkowski separates sharp, narrow tops (Adam) from rounded, wide ones (Eve). Eve & Eve double tops tend to be the more dependable variant. In all cases, wait for the neckline break — an unconfirmed double top is just two highs.",
        ],
      },
    ],
    mistakes: [
      "Shorting the second peak before the neckline breaks.",
      "Requiring the two highs to be identical — a small difference is fine.",
      "Ignoring the higher-timeframe uptrend the pattern is trying to reverse.",
    ],
    faqs: [
      { q: "When is a double top confirmed?", a: "Only when price closes below the valley low between the two peaks (the neckline). Until then it is just two highs." },
      { q: "What is the target for a double top?", a: "Measure the distance from the peaks down to the neckline, then project that same distance below the neckline breakdown." },
    ],
  },
  {
    slug: "falling-wedge-pattern",
    category: "library",
    title: "Falling wedge pattern",
    summary:
      "Two down-sloping, converging trendlines that usually resolve upward. A bullish pattern where selling exhausts into the apex — confirm with a breakout on volume.",
    readMins: 7,
    diagram: {
      type: "pattern", bull: true,
      path: "M10,32 L48,74 L76,50 L118,92 L146,74 L186,106 L210,94 L234,20",
      level: null, points: [[48, 74], [118, 92], [186, 106]], breakout: [214, 78],
      caption: "Falling wedge — converging down-sloping lines, upside breakout.",
    },
    stats: [
      { label: "Typical breakout direction", value: "Up" },
      { label: "Average rise", value: "~+32%" },
      { label: "Throwback rate", value: "~53%" },
      { label: "Bulkowski rank (bullish)", value: "31 / 39" },
    ],
    takeaways: [
      "Both trendlines slope down but converge — lower highs and lower lows narrowing.",
      "Falling wedges are bullish and usually break upward.",
      "Momentum fades even as price drifts lower — a bullish divergence tell.",
      "Confirmation is a close above the upper trendline with volume.",
    ],
    blocks: [
      {
        h: "How it forms",
        p: [
          "Price grinds lower inside two down-sloping lines that gradually converge. Each new low comes with less momentum — the range tightens as sellers run out of conviction. When buyers finally overwhelm the drift, price breaks above the upper trendline and often accelerates.",
        ],
      },
      {
        h: "The trading playbook",
        list: [
          "Entry — on a close above the upper (falling) trendline with a volume expansion, or on the retest of that line.",
          "Stop — below the most recent low inside the wedge.",
          "Target — measure the height at the wedge's widest point and project it up from the breakout.",
        ],
      },
      {
        h: "Wedge vs pennant vs channel",
        p: [
          "A falling wedge's lines both slope down and converge; a bull pennant's converge but come after a vertical pole; a channel's lines stay parallel. The wedge's tell is fading downside momentum into a narrowing range — a hint the decline is running out of fuel.",
        ],
      },
    ],
    mistakes: [
      "Confusing a falling wedge (converging) with a parallel down-channel.",
      "Buying inside the wedge before the upper line breaks.",
      "Trusting a low-volume breakout that fails the retest.",
    ],
    faqs: [
      { q: "Is a falling wedge bullish or bearish?", a: "Bullish. Despite the downward slope, falling wedges usually resolve with an upside breakout as selling pressure exhausts." },
      { q: "Where is the target?", a: "Measure the widest part of the wedge and project that height upward from the breakout point." },
    ],
  },
  {
    slug: "bull-pennant-pattern",
    category: "library",
    title: "Bull pennant pattern",
    summary:
      "A brief converging-triangle pause inside an uptrend after a sharp pole. Breaks up only ~57% of the time, so volume confirmation is essential.",
    readMins: 7,
    diagram: {
      type: "pattern", bull: true,
      path: "M10,110 L40,24 L60,54 L80,34 L98,50 L114,40 L128,46 L140,43 L232,14",
      level: null, points: [[40, 24]], breakout: [188, 30],
      caption: "Bull pennant — steep pole, a tight converging pennant, breakout up.",
    },
    stats: [
      { label: "Upward breakout rate", value: "~57%" },
      { label: "Break-even failure (up)", value: "~54%" },
      { label: "Average rise", value: "~+7%" },
      { label: "Volume decline in formation", value: "~86%" },
    ],
    takeaways: [
      "A steep flagpole up, then a small symmetrical (converging) pennant.",
      "Breaks up only ~57% of the time — demand extra confirmation.",
      "Volume declines during the pennant in ~86% of cases, then spikes on the break.",
      "Target = flagpole height projected from the breakout.",
    ],
    blocks: [
      {
        h: "How it forms",
        p: [
          "After a sharp rally (the pole), price consolidates in a small converging triangle as buyers and sellers briefly balance. Volume dries up as the pennant tightens. A breakout above the upper trendline — ideally on a volume surge — signals the trend is ready to resume.",
        ],
      },
      {
        h: "The trading playbook",
        list: [
          "Entry — on a close above the upper trendline with above-average volume, or on the retest of that line as support.",
          "Stop — below the pennant low, the lower trendline, or the flagpole midpoint.",
          "Target — measure the flagpole height and project it above the breakout point.",
        ],
      },
      {
        h: "Why confirmation matters",
        p: [
          "Bulkowski's data is blunt about pennants: they break upward only about 57% of the time and carry a high break-even failure rate. Flags outperform pennants on nearly every metric, so treat a pennant as a lower-conviction setup and lean on volume and the higher-timeframe trend before committing.",
        ],
      },
    ],
    mistakes: [
      "Trading a pennant with no preceding flagpole — that is just a small triangle.",
      "Letting the pennant run more than ~15 candles or three weeks on daily — then it is a range.",
      "Confusing a pennant (converging) with a flag (parallel).",
    ],
    faqs: [
      { q: "What is the success rate of a bull pennant?", a: "Per Bulkowski, upward breakouts fail to move meaningfully about 54% of the time, and pennants break upward only ~57% of the time — hence the need for volume confirmation." },
      { q: "How long should a bull pennant take to form?", a: "Roughly 15 candles on any timeframe (about one to three weeks on a daily chart). Longer than that and it is no longer a pennant." },
      { q: "Bull pennant vs bull flag — which is better?", a: "The flag. It beats the pennant on break-even and target-hit rates across Bulkowski's sample." },
    ],
  },
  {
    slug: "triple-bottom-pattern",
    category: "library",
    title: "Triple bottom pattern",
    summary:
      "Three failed tests of the same support that build a firm base. ~13% failure, ~46% average rise and a ~74% target-hit rate on 2,500+ Bulkowski trades.",
    readMins: 7,
    diagram: {
      type: "pattern", bull: true,
      path: "M10,26 L45,96 L80,54 L115,96 L150,54 L185,96 L215,54 L235,20",
      level: 54, points: [[45, 96], [115, 96], [185, 96]], breakout: [226, 38],
      caption: "Triple bottom — three equal lows, confirmed on the resistance break.",
    },
    stats: [
      { label: "Break-even failure", value: "~13%" },
      { label: "Average rise", value: "+46%" },
      { label: "Target hit rate", value: "~74%" },
      { label: "Bulkowski rank (bullish)", value: "12 / 39" },
    ],
    takeaways: [
      "Three troughs at a similar low with two peaks between them.",
      "The resistance across the peaks is the confirmation line.",
      "Confirmation is a close above that resistance — a firm base breaking out.",
      "Target = pattern height projected up from the breakout.",
    ],
    blocks: [
      {
        h: "How it forms",
        p: [
          "Price tests the same support level three times and holds each time, carving out a broad, sturdy base. Each failed break lower drains selling pressure. The horizontal line across the two interior peaks is the resistance; a close above it confirms the bullish reversal.",
        ],
      },
      {
        h: "The trading playbook",
        list: [
          "Entry — on a close above the peak resistance, or on the throwback that retests it as new support.",
          "Stop — below the triple-bottom lows.",
          "Target — measure from the lows to the resistance and project that height above the breakout.",
        ],
      },
      {
        h: "Why it is dependable",
        p: [
          "Three successful defences of a level is stronger evidence of a base than one or two. Bulkowski's sample shows a low ~13% failure rate and a ~74% target-hit rate — but the pattern still only pays once resistance breaks, so patience beats anticipation.",
        ],
      },
    ],
    mistakes: [
      "Buying the third bottom before resistance breaks.",
      "Requiring the three lows to be identical — close is enough.",
      "Ignoring volume, which should expand on the breakout.",
    ],
    faqs: [
      { q: "How reliable is a triple bottom?", a: "Very — Bulkowski reports about a 13% break-even failure rate and a ~74% target-hit rate, ranking it 12th of 39 bullish patterns, when confirmed by a resistance breakout." },
      { q: "What confirms a triple bottom?", a: "A decisive close above the resistance line drawn across the two peaks between the three lows." },
    ],
  },
];

export const getLesson = (slug) => LESSONS.find((l) => l.slug === slug);
export const lessonsByCategory = (cat) => LESSONS.filter((l) => l.category === cat);
export const relatedLessons = (lesson) =>
  LESSONS.filter((l) => l.category === lesson.category && l.slug !== lesson.slug);
